/* eslint-disable react/require-default-props */
import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { ModalOverlay } from './ModalOverlay';
import { swallowNextEscapeKeyUp } from './swallowEscapeKeyUp';
import './ModalStyles.scss';
export function Modal({ isOpen, onClose, children, variant = 'base', dismissOnBackdropClick = true, className = '', triggerElementId, ariaLabelledBy, ariaDescribedBy, ariaLabel, container }) {
    if (process.env.NODE_ENV !== 'production' && !ariaLabel && !ariaLabelledBy) {
        // eslint-disable-next-line no-console
        console.error('Modal requires either ariaLabel or ariaLabelledBy for an accessible name.');
    }
    const containerRef = useRef(null);
    const previousFocusRef = useRef(null);
    const previousBodyOverflowRef = useRef(null);
    const [isShaking, setIsShaking] = useState(false);
    // Capture the trigger element on open and restore focus on close
    useEffect(() => {
        var _a;
        if (isOpen) {
            if (triggerElementId) {
                previousFocusRef.current = document.getElementById(triggerElementId);
            }
            else {
                previousFocusRef.current = document.activeElement;
            }
            // Focus the modal container on open
            requestAnimationFrame(() => {
                var _a;
                (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            });
            // Prevent body scroll (skipped for anchored modals — body isn't covered)
            if (!container) {
                previousBodyOverflowRef.current = document.body.style.overflow;
                document.body.style.overflow = 'hidden';
            }
        }
        else {
            // Restore focus to trigger element (always, including anchored modals)
            (_a = previousFocusRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            previousFocusRef.current = null;
            if (!container && previousBodyOverflowRef.current !== null) {
                document.body.style.overflow = previousBodyOverflowRef.current;
            }
            previousBodyOverflowRef.current = null;
        }
        return () => {
            if (previousBodyOverflowRef.current !== null && !container) {
                document.body.style.overflow = previousBodyOverflowRef.current;
                previousBodyOverflowRef.current = null;
            }
        };
    }, [isOpen, triggerElementId, container]);
    // Esc key handler
    useEffect(() => {
        if (!isOpen)
            return undefined;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                // This Escape is the Modal's. The keyup that finishes the press must not reach a host
                // panel (Brushaway) that closes on keyup, or one key closes both.
                swallowNextEscapeKeyUp();
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    // Focus trap
    useEffect(() => {
        if (!isOpen)
            return undefined;
        const handleTabKey = (e) => {
            if (e.key !== 'Tab' || !containerRef.current)
                return;
            const focusableElements = containerRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focusableElements.length === 0) {
                e.preventDefault();
                containerRef.current.focus();
                return;
            }
            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === firstFocusable) {
                    e.preventDefault();
                    lastFocusable === null || lastFocusable === void 0 ? void 0 : lastFocusable.focus();
                }
            }
            else if (document.activeElement === lastFocusable) {
                e.preventDefault();
                firstFocusable === null || firstFocusable === void 0 ? void 0 : firstFocusable.focus();
            }
        };
        window.addEventListener('keydown', handleTabKey);
        return () => window.removeEventListener('keydown', handleTabKey);
    }, [isOpen]);
    const handleShake = useCallback(() => {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 400);
    }, []);
    if (!isOpen)
        return null;
    const isFullscreen = variant === 'fullscreen';
    const isAnchored = Boolean(container);
    const containerClasses = [
        'qmb-ui-modal',
        `qmb-ui-modal--${variant}`,
        isShaking ? 'qmb-ui-modal--shake' : '',
        className
    ]
        .filter(Boolean)
        .join(' ');
    const wrapperClasses = ['qmb-ui-modal-wrapper', isAnchored ? 'qmb-ui-modal-wrapper--anchored' : '']
        .filter(Boolean)
        .join(' ');
    const modalContent = (React.createElement("div", { className: wrapperClasses },
        !isFullscreen && (React.createElement(ModalOverlay, { onClose: onClose, dismissOnBackdropClick: dismissOnBackdropClick, onShake: handleShake })),
        React.createElement("div", { ref: containerRef, className: containerClasses, role: "dialog", "aria-modal": "true", "aria-labelledby": ariaLabel ? undefined : ariaLabelledBy, "aria-describedby": ariaDescribedBy, "aria-label": ariaLabel, tabIndex: -1 }, children)));
    return createPortal(modalContent, container !== null && container !== void 0 ? container : document.body);
}
